import { useState } from "react";
import { Bot, RefreshCw } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { AppLayout } from "@/components/layout/AppLayout";
import { ItemList } from "@/components/items/ItemList";
import { CashInCard } from "@/components/items/CashInCard";
import { ProjectHealthCards } from "@/components/projects/ProjectHealthCards";
import { Button } from "@/components/ui/button";
import { useItemStats, useDoneThisWeek } from "@/hooks/useItems";
import { useActiveJobs } from "@/hooks/useAgentJobs";
import { usePrioritize } from "@/hooks/usePrioritize";
import { StatsBar } from "@/components/dashboard/StatsBar";
import { DailyTargetBanner } from "@/components/dashboard/DailyTargetBanner";
import { CalendarWidget } from "@/components/dashboard/CalendarWidget";
import { AgentFeed } from "@/components/dashboard/AgentFeed";
import { OnboardingWizard } from "@/components/onboarding/OnboardingWizard";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { formatError } from "@/lib/utils";

const ONBOARDING_STORAGE_KEY = "ho-onboarding-complete";

export default function Dashboard() {
  const { user } = useAuth();
  const { data: stats } = useItemStats();
  const { data: doneCount } = useDoneThisWeek();
  const { data: activeJobs } = useActiveJobs();
  const prioritize = usePrioritize();

  // Show onboarding if: user exists, localStorage flag not set, and user has zero items
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => localStorage.getItem(ONBOARDING_STORAGE_KEY) === "true"
  );

  const showOnboarding =
    !onboardingDismissed &&
    !!user &&
    stats !== undefined &&
    stats.total === 0;

  const handlePrioritize = () => {
    prioritize.mutate(undefined, {
      onSuccess: (data) => toast(`Reprioritized ${data?.items_updated || 0} items`),
      onError: (err) => toast.error(formatError(err)),
    });
  };

  return (
    <AppLayout>
      <AnimatePresence>
        {showOnboarding && (
          <OnboardingWizard
            onDismiss={() => setOnboardingDismissed(true)}
          />
        )}
      </AnimatePresence>
      <StatsBar />
      <DailyTargetBanner />
      <CalendarWidget />
      {stats && stats.total > 0 && (
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-muted-foreground">
            {stats.total} item{stats.total !== 1 ? "s" : ""}
            {doneCount ? ` · ${doneCount} done this week` : ""}
            {activeJobs && activeJobs.length > 0 && (
              <span className="inline-flex items-center gap-1 ml-2 text-primary">
                <Bot className="h-3 w-3" />
                {activeJobs.length} job{activeJobs.length !== 1 ? "s" : ""}
              </span>
            )}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePrioritize}
            disabled={prioritize.isPending}
            className="text-xs text-muted-foreground"
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${prioritize.isPending ? "animate-spin" : ""}`} />
            {prioritize.isPending ? "Reprioritizing..." : "Reprioritize"}
          </Button>
        </div>
      )}

      <CashInCard />

      <section className="mb-8">
        <ItemList />
      </section>

      <section className="mb-8">
        <AgentFeed />
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-3">Project Health</h2>
        <ProjectHealthCards />
      </section>
    </AppLayout>
  );
}
